/**
 * Company identity shown in the footer.
 *
 * Both the legal entity name and the year follow the user's location as recorded
 * in the database (Users.Location -> LocationsMaster), not where the browser
 * happens to be. A user travelling abroad therefore keeps seeing their own
 * entity and their own region's year - the footer states what the system holds
 * for them, which is the same source HR and payroll use.
 *
 * locationId is written to localStorage at login by both paths: src/App.tsx
 * (MSAL, used in qa/prod) and src/pages/login.tsx (the dev login page).
 */

/** LocationsMaster.LocationID values. Only these two exist. */
export const LOCATION_USA = 1
export const LOCATION_INDIA = 2

/** Used when the user has no location on record - Users.Location is nullable. */
export const DEFAULT_LOCATION = LOCATION_INDIA

interface CompanyIdentity {
  /** Legal entity name for the copyright line. */
  name: string
  /** IANA zone the year is reckoned in. Only matters around 31 December. */
  timeZone: string
}

// The two names differ in the first word only: Unique (US) vs Unite (India).
const ENTITIES: Record<number, CompanyIdentity> = {
  [LOCATION_USA]: {
    name: "Unique Computech Info Pvt. Ltd.",
    timeZone: "America/New_York",
  },
  [LOCATION_INDIA]: {
    name: "Unite Computech Info Pvt. Ltd.",
    timeZone: "Asia/Kolkata",
  },
}

/** Reads the stored location, falling back to the default. */
function getLocationId(): number {
  try {
    const raw = localStorage.getItem("locationId")
    if (raw === null || raw.trim() === "") return DEFAULT_LOCATION
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed in ENTITIES ? parsed : DEFAULT_LOCATION
  } catch {
    // Storage unavailable (private browsing, blocked site data) - fall back
    // rather than letting the footer throw.
    return DEFAULT_LOCATION
  }
}

/**
 * Company name and copyright year for the signed-in user.
 *
 * The year is the current year in that location's time zone. For roughly ten
 * hours on 31 December the two regions differ; the rest of the year they agree.
 */
export function getFooterIdentity(): { companyName: string; year: string } {
  const entity = ENTITIES[getLocationId()] ?? ENTITIES[DEFAULT_LOCATION]

  let year: string
  try {
    year = new Intl.DateTimeFormat("en-US", {
      timeZone: entity.timeZone,
      year: "numeric",
    }).format(new Date())
  } catch {
    // Unknown zone on an old engine - the local year is close enough to never show nothing.
    year = String(new Date().getFullYear())
  }

  return { companyName: entity.name, year }
}
